import React, { createContext, useContext, useMemo } from "react";
import ar from "@/locales/ar.json";
import en from "@/locales/en.json";

export const LOCALES = { ar, en };
export const SUPPORTED_LANGS = ["ar", "en"];
export const DEFAULT_LANG = "ar";
export const LANG_STORAGE_KEY = "dbdg_lang";

const I18nContext = createContext(null);

function resolve(dict, key) {
  if (!dict) return undefined;
  return key.split(".").reduce((acc, part) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[part];
  }, dict);
}

export function isSupportedLang(lang) {
  return SUPPORTED_LANGS.includes(lang);
}

export function I18nProvider({ lang, children }) {
  const value = useMemo(() => {
    const safeLang = isSupportedLang(lang) ? lang : DEFAULT_LANG;
    const dict = LOCALES[safeLang];

    const t = (key, fallback = "") => {
      const found = resolve(dict, key);
      if (typeof found === "string") return found;
      if (found === undefined || found === null) {
        const backup = resolve(LOCALES[DEFAULT_LANG], key);
        if (typeof backup === "string") return backup;
        return fallback || key;
      }
      return found;
    };

    const tList = (key) => {
      const found = resolve(dict, key);
      return Array.isArray(found) ? found : [];
    };

    return { lang: safeLang, dict, t, tList };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    const dict = LOCALES[DEFAULT_LANG];
    return {
      lang: DEFAULT_LANG,
      dict,
      t: (key, fallback = "") => {
        const found = resolve(dict, key);
        return typeof found === "string" ? found : fallback || key;
      },
      tList: (key) => {
        const found = resolve(dict, key);
        return Array.isArray(found) ? found : [];
      },
    };
  }
  return ctx;
}

export function storeLang(lang) {
  if (typeof window === "undefined" || !isSupportedLang(lang)) return;
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch (e) {
    /* storage unavailable */
  }
}

export function readStoredLang() {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    return isSupportedLang(stored) ? stored : null;
  } catch (e) {
    return null;
  }
}
